import moment from "moment";
import { SuperVisor, Ticket, TicketAge, User } from "@/types";
import { SupervisorService } from "@/services";
import { S3_URL } from "@/Config";
import Papa from 'papaparse';
import { getRolePermissions } from '@/utils/rolePermissions';
export const getUserRoleName = (user: SuperVisor | null) => {
  const permissions = getRolePermissions(user);

  if (permissions.isDepartmentManager && permissions.department) {
    return `${permissions.department.charAt(0).toUpperCase() + permissions.department.slice(1)} manager`;
  }
  if (user) {
    if (permissions.isPlatformSuperAdmin) return "Super Admin";
    else if (permissions.isSystemAdmin || permissions.isDepartmentManager) return "System Admin";
    else if (permissions.isAgent) return "Agent";
    else if (permissions.isCustomer) return "Customer";
    else if (permissions.isGroupUser) {
      const role = permissions.isGroupManager ? "Group Manager" : "Group Member";
      const rawGroupName = user.group?.groupName || user.groupName || user.agentType;
      const groupName = rawGroupName
        ?.split(/[_\s]+/)
        .filter(Boolean)
        .map((part) => part.length <= 2
          ? part.toUpperCase()
          : `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(" ");
      return groupName ? `${role} — ${groupName}` : role;
    }
  }
  return "";
};

export const formatToCustomISO = (date: Date) => {
  const pad = (num: number) => num.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // Use "+" instead of " " between date and time
  return `${year}-${month}-${day}+${hours}:${minutes}:${seconds}`;
};

export const parseDate = (dateStr: string) => {
  const [datePart] = dateStr.split('+'); // Remove timezone if present
  return new Date(datePart);
};

export const formatPauseTime = (seconds: string) => {
  if (!seconds) return "0:00:00";
  const secs = parseInt(seconds);
  const hrs = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const sec = secs % 60;
  return `${hrs}:${mins.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};

export const getUserName = (user: User | SuperVisor | null | undefined) => user?.fullName || user?.userName || user?.firstName || user?.email || ""
export const getFormattedDate = (date: Date | null | undefined = new Date()) => moment(date).format("DD MMM YYYY")
export const getFormattedDateTime = (date: Date | null | undefined = new Date()) => moment(date).format("DD MMM YYYY hh:mm a")
export const getFormattedTime = (date: Date | null | undefined = new Date()) => moment(date).format("hh:mm a")

export const getTicketStatus = (ticket: Ticket | null) => {
  const statusMap: { [key: string]: string } = {
    escalated: 'Escalated',
    open: 'Open',
    in_progress: 'In Progress',
    on_hold: 'On Hold',
    resolved: 'Resolved',
    closed: 'Closed',
    re_open: 'Re Opened',
  };

  return statusMap[ticket?.status ?? ''] || 'Unknown'
}

export const getTicketClass = (ticket: Ticket | null) => {
  const classMap: { [key: string]: string } = {
    escalated: 'borderRed',
    re_open: 'borderRed',
    open: 'lightRed',
    on_hold: 'lightYellow',
    resolved: 'lightGreen',
    closed: 'lightGreen',
  };

  return classMap[ticket?.status ?? ''] || 'lightPurple';
};

export const formatPrice = (price: number | string | null | undefined): string => {
  if (!price) return `0.00`

  // Convert to number and format with commas
  const number = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;

  return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

// Utility function to format minutes into years, months, days, hours, and minutes
export const formatResolutionTime = (minutes: number | null | undefined) => {
  if (minutes) {
    // Convert minutes into respective time units
    const yearInMinutes = 525600; // Approximate minutes in a year (365 days)
    const monthInMinutes = 43200; // Approximate minutes in a month (30 days)
    const dayInMinutes = 1440;    // Minutes in a day
    const hourInMinutes = 60;     // Minutes in an hour

    const years = Math.floor(minutes / yearInMinutes);
    minutes %= yearInMinutes;

    const months = Math.floor(minutes / monthInMinutes);
    minutes %= monthInMinutes;

    const days = Math.floor(minutes / dayInMinutes);
    minutes %= dayInMinutes;

    const hours = Math.floor(minutes / hourInMinutes);
    const mins = minutes % hourInMinutes;

    const resultParts = [];
    if (years) resultParts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months) resultParts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days) resultParts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours) resultParts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    // Always show minutes if nothing else is present
    if (mins || resultParts.length === 0) resultParts.push(`${mins} min${mins > 1 ? 's' : ''}`);

    return resultParts.join(' ');
  } else return ''
};

export const formatTicketAge = (ticketAge?: TicketAge) => {
  const display = ticketAge?.display?.trim();

  if (display) {
    const matches = Array.from(display.matchAll(/(\d+(?:\.\d+)?)\s*(d|h|m|min|mins)\b/gi));

    if (matches.length) {
      return matches.map((match) => {
        const value = match[1];
        const numericValue = Number(value);
        const unit = match[2].toLowerCase();

        if (unit === 'd') return `${value} day${numericValue === 1 ? '' : 's'}`;
        if (unit === 'h') return `${value} hour${numericValue === 1 ? '' : 's'}`;
        return `${value} min${numericValue === 1 ? '' : 's'}`;
      }).join(' ');
    }

    return display;
  }

  if (typeof ticketAge?.minutes === 'number') {
    return formatResolutionTime(ticketAge.minutes) || '-';
  }

  return '-';
};


export const generateId = () => Number(`${String(Date.now()).slice(-8)}${Math.random().toString().substring(2, 8)}`)


// file upload helper
export async function uploadFilesAndGetLinks(
  files: File[],
  chatBotId?: string
): Promise<string[]> {
  const uploadPromises = files.map(async (file: File) => {
    const [fileName, ext = 'png'] = file.name.split(/\.(?=[^.]+$)/);

    // Request presigned upload info from backend
    const response = await SupervisorService.getUploadLink({
      filename_frontend: fileName,
      extension: ext,
      chatBotId,
    });

    const { provider, presignedUrl, filePath, formData, publicUrl } = response.data;

    if (provider === 'cloudinary') {
      const data = new FormData();
      data.append("file", file);
      for (const key in formData) {
        data.append(key, formData[key]);
      }

      const uploadResp = await fetch(presignedUrl, {
        method: "POST",
        body: data,
      });

      if (!uploadResp.ok) {
        throw new Error(`Cloudinary upload failed: ${file.name}`);
      }

      return publicUrl;
    }

    if (provider === 'azure') {
      const uploadResp = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || `application/octet-stream`,
        },
      });

      if (!uploadResp.ok) {
        throw new Error(`Azure Blob upload failed: ${file.name}`);
      }

      return publicUrl || presignedUrl.split("?")[0]; // Strip SAS token for clean URL
    }

    // Default to S3-style upload
    const uploadResp = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type || `image/${ext}`,
      },
    });

    if (!uploadResp.ok) {
      throw new Error(`S3 upload failed: ${file.name}`);
    }

    return publicUrl || `${S3_URL}/${filePath}`;
  });

  return Promise.all(uploadPromises);
}

// Day-boundary helpers (local time). Use these so a selected range is
// inclusive of the whole day instead of collapsing to a single midnight
// instant (which produced a zero-width window => "No data available").
export const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Date presets (Today, This Month, Last Quarter, etc.)
// Every preset returns startDate at start-of-day and endDate at end-of-day
// so the range is fully inclusive regardless of the current clock time.
export const datePresets = [
  {
    value: 'today',
    label: 'Today',
    getRange: () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return {
        startDate: startOfDay(d),
        endDate: endOfDay(d),
      };
    },
  },
  {
    value: 'this_month',
    label: 'This Month',
    getRange: () => ({
      startDate: startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
      endDate: endOfDay(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)),
    }),
  },
  {
    value: 'last_month',
    label: 'Last Month',
    getRange: () => ({
      startDate: startOfDay(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)),
      endDate: endOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 0)),
    }),
  },
  {
    value: 'this_quarter',
    label: 'This Quarter',
    getRange: () => {
      const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
      const startMonth = (currentQuarter - 1) * 3;
      return {
        startDate: startOfDay(new Date(new Date().getFullYear(), startMonth, 1)),
        endDate: endOfDay(new Date(new Date().getFullYear(), startMonth + 3, 0)),
      };
    },
  },
  {
    value: 'last_quarter',
    label: 'Last Quarter',
    getRange: () => {
      const lastQuarter = Math.floor(new Date().getMonth() / 3) || 4; // Handle Q1 → Q4 of last year
      const startMonth = (lastQuarter - 1) * 3;
      const year = lastQuarter === 4 ? new Date().getFullYear() - 1 : new Date().getFullYear();
      return {
        startDate: startOfDay(new Date(year, startMonth, 1)),
        endDate: endOfDay(new Date(year, startMonth + 3, 0)),
      };
    },
  },
  {
    value: 'this_year',
    label: 'This Year',
    getRange: () => ({
      startDate: startOfDay(new Date(new Date().getFullYear(), 0, 1)),
      endDate: endOfDay(new Date(new Date().getFullYear(), 11, 31)),
    }),
  },
  {
    value: 'last_year',
    label: 'Last Year',
    getRange: () => ({
      startDate: startOfDay(new Date(new Date().getFullYear() - 1, 0, 1)),
      endDate: endOfDay(new Date(new Date().getFullYear() - 1, 11, 31)),
    }),
  },
  {
    value: 'custom',
    label: 'Custom Range',
    getRange: () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
];

export const slaOptions = [
  { value: 'yes', label: 'Sla Breached' },
  { value: 'no', label: 'Sla Met' },
];

// Source options (WhatsApp, IVR, Mail, etc.)
export const sourceOptions = [
  { value: 'whatsappchat', label: 'Whatsapp' },
  { value: 'ivr', label: 'Ivr' },
  { value: 'email', label: 'Email' },
  { value: 'chatbot', label: 'Chatbot' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
];

export function exportCSV(fileName: string, header: string[], body: []) {
  const csv = Papa.unparse({
    fields: header, // column labels
    data: body, // row values
  });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const DealStageOptions = [
  { value: "Prospecting", label: "Prospecting", probabilty: "0%" },
  { value: "Qualifying", label: "Qualifying", probabilty: "5%" },
  { value: "Initial Contact and Discovery", label: "Initial Contact and Discovery", probabilty: "10%" },
  { value: "Presentation/Demonstration", label: "Presentation/Demonstration", probabilty: "20%" },
  { value: "Quote Submitted", label: "Quote Submitted", probabilty: "40%" },
  { value: "Handling Objections", label: "Handling Objections", probabilty: "60%" },
  { value: "Negotiation", label: "Negotiation", probabilty: "80%" },
  { value: "Closed", label: "Closed", probabilty: "100%" },
  { value: "Lost", label: "Lost", probabilty: "0%" },
];


export const PriorityOptions = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
]

export const SourceOptions = [
  { value: "Referral", label: "Referral" },
  { value: "Website", label: "Website" },
  { value: "Social Media", label: "Social Media" },
  { value: "Email", label: "Email" },
  { value: "Other", label: "Other" },
]

// Naira leads the list: it is the platform currency and the deal-form default.
export const currencyList = [
  { country: "Nigeria", label: "NGN (₦)", currency: "Nigerian Naira", value: "₦" },
  { country: "United States", label: "USD ($)", currency: "US Dollar", value: "$" },
  { country: "United Kingdom", label: "GBP (£)", currency: "Pound Sterling", value: "£" },
  { country: "European Union", label: "EUR (€)", currency: "Euro", value: "€" },
  { country: "India", label: "INR (₹)", currency: "Indian Rupee", value: "₹" },
  { country: "Australia", label: "AUD (A$)", currency: "Australian Dollar", value: "A$" },
  { country: "Canada", label: "CAD (CA$)", currency: "Canadian Dollar", value: "CA$" },
  { country: "Japan", label: "JPY (¥)", currency: "Japanese Yen", value: "¥" },
  { country: "China", label: "CNY (¥)", currency: "Yuan Renminbi", value: "¥" },
  { country: "Singapore", label: "SGD (S$)", currency: "Singapore Dollar", value: "S$" },
  { country: "United Arab Emirates", label: "AED (د.إ)", currency: "UAE Dirham", value: "د.إ" },
  { country: "Saudi Arabia", label: "SAR (﷼)", currency: "Saudi Riyal", value: "﷼" },
  { country: "Switzerland", label: "CHF (CHF)", currency: "Swiss Franc", value: "CHF" },
  { country: "Russia", label: "RUB (₽)", currency: "Russian Ruble", value: "₽" },
  { country: "Brazil", label: "BRL (B$)", currency: "Brazilian Real", value: "R$" },
  { country: "South Africa", label: "ZAR (Z$)", currency: "South African Rand", value: "R" },
  { country: "Mexico", label: "MXN (MX$)", currency: "Mexican Peso", value: "MX$" },
  { country: "New Zealand", label: "NZD (NZ$)", currency: "New Zealand Dollar", value: "NZ$" },
  { country: "South Korea", label: "KRW (₩)", currency: "South Korean Won", value: "₩" },
  { country: "Turkey", label: "TRY (₺)", currency: "Turkish Lira", value: "₺" },
  { country: "Indonesia", label: "IDR (Rp)", currency: "Indonesian Rupiah", value: "Rp" },
  { country: "Thailand", label: "THB (฿)", currency: "Thai Baht", value: "฿" },
  { country: "Bangladesh", label: "BDT (৳)", currency: "Bangladeshi Taka", value: "৳" },
  { country: "Pakistan", label: "PKR (₨)", currency: "Pakistani Rupee", value: "₨" },
  { country: "Sri Lanka", label: "LKR (Rs)", currency: "Sri Lankan Rupee", value: "Rs" },
  { country: "Malaysia", label: "MYR (RM)", currency: "Malaysian Ringgit", value: "RM" },
  { country: "Philippines", label: "PHP (₱)", currency: "Philippine Peso", value: "₱" },
  { country: "Vietnam", label: "VND (₫)", currency: "Vietnamese Dong", value: "₫" },
  { country: "Egypt", label: "EGP (E£)", currency: "Egyptian Pound", value: "E£" },
  { country: "Kenya", label: "KES (KSh)", currency: "Kenyan Shilling", value: "KSh" },
  { country: "Qatar", label: "QAR (﷼)", currency: "Qatari Riyal", value: "﷼" },
  { country: "Kuwait", label: "KWD (د.ك)", currency: "Kuwaiti Dinar", value: "د.ك" },
  { country: "Oman", label: "OMR (﷼)", currency: "Omani Rial", value: "﷼" },
  { country: "Bahrain", label: "BHD (د.ب)", currency: "Bahraini Dinar", value: ".د.ب" },
  { country: "Nepal", label: "NPR (₨)", currency: "Nepalese Rupee", value: "₨" },
  { country: "Bhutan", label: "BTN (Nu.)", currency: "Ngultrum", value: "Nu." },
  { country: "Maldives", label: "MVR (Rf)", currency: "Rufiyaa", value: "Rf" },
  { country: "Israel", label: "ILS (₪)", currency: "Israeli Shekel", value: "₪" },
  { country: "Jordan", label: "JOD (د.ا)", currency: "Jordanian Dinar", value: "د.ا" },
  { country: "Lebanon", label: "LBP (ل.ل)", currency: "Lebanese Pound", value: "ل.ل" },
  { country: "Iran", label: "IRR (﷼)", currency: "Iranian Rial", value: "﷼" },
  { country: "Iraq", label: "IQD (ع.د)", currency: "Iraqi Dinar", value: "ع.د" },
  { country: "Argentina", label: "ARS ($)", currency: "Argentine Peso", value: "$" },
  { country: "Chile", label: "CLP ($)", currency: "Chilean Peso", value: "$" },
  { country: "Colombia", label: "COP ($)", currency: "Colombian Peso", value: "$" },
  { country: "Peru", label: "PEN (S/)", currency: "Peruvian Sol", value: "S/" },
  { country: "Venezuela", label: "VES (Bs)", currency: "Bolívar", value: "Bs" },
  { country: "Hong Kong", label: "HKD (HK$)", currency: "Hong Kong Dollar", value: "HK$" },
  { country: "Taiwan", label: "TWD (NT$)", currency: "New Taiwan Dollar", value: "NT$" },
  { country: "Morocco", label: "MAD (DH)", currency: "Moroccan Dirham", value: "DH" },
  { country: "Algeria", label: "DZD (دج)", currency: "Algerian Dinar", value: "دج" },
  { country: "Ethiopia", label: "ETB (Br)", currency: "Ethiopian Birr", value: "Br" },
  { country: "Ghana", label: "GHS (₵)", currency: "Ghanaian Cedi", value: "₵" },
  { country: "Tanzania", label: "TZS (TSh)", currency: "Tanzanian Shilling", value: "TSh" },
  { country: "Uganda", label: "UGX (USh)", currency: "Ugandan Shilling", value: "USh" },
];

export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" }
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
  { value: "separated", label: "Separated" },
  { value: "partnered", label: "Partnered" },
]


export const LANGUAGE_OPTIONS = [
  { value: "english", label: "English" },
  { value: "spanish", label: "Spanish" },
  { value: "french", label: "French" },
  { value: "german", label: "German" },
  { value: "italian", label: "Italian" },
  { value: "portuguese", label: "Portuguese" },
  { value: "russian", label: "Russian" },
  { value: "chinese", label: "Chinese (Simplified)" },
  { value: "traditional_chinese", label: "Chinese (Traditional)" },
  { value: "japanese", label: "Japanese" },
  { value: "korean", label: "Korean" },
  { value: "hindi", label: "Hindi" },
  { value: "arabic", label: "Arabic" },
  { value: "bengali", label: "Bengali" },
  { value: "urdu", label: "Urdu" },
  { value: "turkish", label: "Turkish" },
  { value: "dutch", label: "Dutch" },
  { value: "polish", label: "Polish" },
  { value: "swedish", label: "Swedish" },
  { value: "norwegian", label: "Norwegian" },
  { value: "danish", label: "Danish" },
  { value: "finnish", label: "Finnish" },
  { value: "greek", label: "Greek" },
  { value: "hebrew", label: "Hebrew" },
  { value: "thai", label: "Thai" },
  { value: "vietnamese", label: "Vietnamese" },
  { value: "indonesian", label: "Indonesian" },
  { value: "malay", label: "Malay" },
  { value: "tamil", label: "Tamil" },
  { value: "telugu", label: "Telugu" },
  { value: "kannada", label: "Kannada" },
  { value: "malayalam", label: "Malayalam" },
  { value: "marathi", label: "Marathi" },
  { value: "gujarati", label: "Gujarati" },
  { value: "punjabi", label: "Punjabi" },
  { value: "odia", label: "Odia" }
];


export const getSLaTimeLabel = (hours: number, minutes: number): string => {
  const h = hours > 0 ? `${hours}h` : '';
  const m = minutes > 0 ? `${minutes.toString().padStart(2, '0')}m` : '';

  if (h && m) return `${h} ${m}`;
  return h || m || '0m';
};

export const formatMinutes = (totalMinutes: number) => {
  if (totalMinutes == null || isNaN(totalMinutes) || totalMinutes < 0) {
    return '0';
  }

  const minutes = Math.floor(totalMinutes);

  const days = Math.floor(minutes / 1440); // 24 * 60
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;

  const parts = [];

  if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours) parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
  if (mins || parts.length === 0) parts.push(`${mins} min${mins > 1 ? 's' : ''}`);

  return parts.join(' ');
};

export const FormatTo12Hour = (time: string): string => {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;

  return `${String(formattedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const monthStart = (year: number, month: number) =>
  new Date(year, month, 1);

const monthEnd = (year: number, month: number) =>
  new Date(year, month + 1, 0);

export const selectDateOptions = [
  {
    value: 'today',
    label: 'Today',
    getRange: () => {
      const today = new Date();
      return {
        startDate: formatDate(today),
        endDate: formatDate(today),
      };
    },
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return {
        startDate: formatDate(d),
        endDate: formatDate(d),
      };
    },
  },
  {
    value: 'this_month',
    label: 'This Month',
    getRange: () => {
      const now = new Date();
      return {
        startDate: formatDate(monthStart(now.getFullYear(), now.getMonth())),
        endDate: formatDate(monthEnd(now.getFullYear(), now.getMonth())),
      };
    },
  },
  {
    value: 'last_month',
    label: 'Last Month',
    getRange: () => {
      const now = new Date();
      return {
        startDate: formatDate(monthStart(now.getFullYear(), now.getMonth() - 1)),
        endDate: formatDate(monthEnd(now.getFullYear(), now.getMonth() - 1)),
      };
    },
  },
  {
    value: 'this_quarter',
    label: 'This Quarter',
    getRange: () => {
      const now = new Date();
      const quarter = Math.floor(now.getMonth() / 3);
      const startMonth = quarter * 3;
      return {
        startDate: formatDate(monthStart(now.getFullYear(), startMonth)),
        endDate: formatDate(monthEnd(now.getFullYear(), startMonth + 2)),
      };
    },
  },
  {
    value: 'last_quarter',
    label: 'Last Quarter',
    getRange: () => {
      const now = new Date();
      let quarter = Math.floor(now.getMonth() / 3) - 1;
      let year = now.getFullYear();

      if (quarter < 0) {
        quarter = 3;
        year -= 1;
      }

      const startMonth = quarter * 3;
      return {
        startDate: formatDate(monthStart(year, startMonth)),
        endDate: formatDate(monthEnd(year, startMonth + 2)),
      };
    },
  },
  {
    value: 'this_year',
    label: 'This Year',
    getRange: () => {
      const year = new Date().getFullYear();
      return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
      };
    },
  },
  {
    value: 'last_year',
    label: 'Last Year',
    getRange: () => {
      const year = new Date().getFullYear() - 1;
      return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
      };
    },
  },
];

export const dropDownStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: '60px',
    fontSize: 'inherit',
    backgroundColor: '#F6F6F6',
    borderRadius: '8px',
    // marginBottom: "16px",
    paddingInline: "8px",
    '.css-1wa3eu0-placeholder': {
      color: '#606B85',
      fontSize: 'inherit'
    },
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: '#d4e1f5',
    borderRadius: '4px',
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: 0,
    marginBottom: 0,
    zIndex: 11
  }),
};

/** Actions the shared `Reducer` understands. */
export type ReducerAction =
    | { type: 'SETDATA'; payload: { name: string; value: unknown } }
    | { type: 'MULTISET_STATE'; multiPayload: { key: string; value: unknown }[] }
    | { type: 'RESET'; payload: unknown };

/** Sets a single key on shared-reducer state. */
export const setField = (name: string, value: unknown): ReducerAction => ({
    type: 'SETDATA',
    payload: { name, value },
});

/** Sets several keys in one render, so related updates do not cascade. */
export const setFields = (fields: Record<string, unknown>): ReducerAction => ({
    type: 'MULTISET_STATE',
    multiPayload: Object.entries(fields).map(([key, value]) => ({ key, value })),
});

export const Reducer = (state: any, action: any) => {
    switch (action.type) {
        case 'SETDATA': {
            const target = action.payload;
            if (!target) {
                return state;
            }
            return {
                ...state,
                [target.name]: target.value,
            };
        }
        case 'MULTISET_STATE':
            return {
                ...state,
                ...(action.multiPayload ?? []).reduce((acc: any, { key, value }: { key: string; value: any }) => ({
                    ...acc,
                    [key]: value
                }), {})
            };
        case 'RESET':
            return action.payload;

        default: {
            return { ...state };
        }
    }
};
